import {
  BookOpen,
  ChevronRight,
  Crosshair,
  Dumbbell,
  ExternalLink,
  Link2,
  Shield,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { PLAYER_TRAINING, TEAM_TOPICS, trainingForWeakness } from './trainingLibrary';
import { SessionLibrary } from './SessionLibrary';
import { DevelopmentProgress } from './DevelopmentProgress';

type DevelopmentSignal = {
  playerId: string;
  weakness: string;
  domain: 'technical' | 'tactical' | 'physical' | 'mental';
  severity: number;
  note: string;
  matchId: string;
  updatedAt: string;
};

function signalKey(teamId: string, playerId: string) {
  return `ecc_development_signal_${teamId}_${playerId}`;
}

function readSignals(teamId: string, playerId: string): DevelopmentSignal[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(signalKey(teamId, playerId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function TrainingCentre() {
  const { t } = useI18n();
  const { team, players, loading, error } = useData();
  const [mode, setMode] = useState<'player' | 'team'>('player');
  const [selectedPlayerId, setSelectedPlayerId] = useState(players[0]?.id || '');
  const [manualWeakness, setManualWeakness] = useState('first_touch');
  const [teamTopicId, setTeamTopicId] = useState(TEAM_TOPICS[0].id);

  const selectedPlayer =
    players.find((player) => player.id === selectedPlayerId) ||
    players[0] ||
    null;

  const signals = useMemo(
    () => team && selectedPlayer ? readSignals(team.id, selectedPlayer.id) : [],
    [team, selectedPlayer],
  );

  const selectedWeakness = signals[0]?.weakness || manualWeakness;
  const method = trainingForWeakness(selectedWeakness);
  const teamTopic = TEAM_TOPICS.find((topic) => topic.id === teamTopicId) || TEAM_TOPICS[0];

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="training-centre-page">
      <section className="metal-card training-hero">
        <div>
          <p className="eyebrow">TRAINING INTELLIGENCE</p>
          <h1>{t.training}</h1>
          <p className="muted">{t.trainingSubtitle}</p>
        </div>

        <div className="training-mode-switch">
          <button className={mode === 'player' ? 'active' : ''} onClick={() => setMode('player')}>
            <Target size={18} />
            {t.individualTraining}
          </button>
          <button className={mode === 'team' ? 'active' : ''} onClick={() => setMode('team')}>
            <Users size={18} />
            {t.teamTraining}
          </button>
        </div>
      </section>

      {mode === 'player' ? (
        <>
          <section className="metal-card player-training-selector">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.selectPlayer}</p>
                <h2>{t.playerDevelopment}</h2>
              </div>
              <Dumbbell size={24} />
            </div>

            <div className="training-player-strip">
              {players.map((player) => (
                <button
                  key={player.id}
                  className={selectedPlayer?.id === player.id ? 'selected' : ''}
                  onClick={() => setSelectedPlayerId(player.id)}
                >
                  <span>
                    {player.photo_url
                      ? <img src={player.photo_url} alt="" />
                      : <ClubLogo club="Polonia Glasgow" className="training-player-logo" />}
                  </span>
                  <strong>#{player.squad_number ?? '—'} {player.name}</strong>
                  <small>{player.position || '—'}</small>
                </button>
              ))}
            </div>
          </section>

          {selectedPlayer && (
            <section className="training-player-sheet-layout">
              <article className="metal-card training-player-sheet">
                <div className="training-player-hero">
                  <span>
                    {selectedPlayer.photo_url
                      ? <img src={selectedPlayer.photo_url} alt="" />
                      : <ClubLogo club="Polonia Glasgow" className="training-sheet-logo" />}
                  </span>
                  <div>
                    <p className="eyebrow">{t.trainingPlayerSheet}</p>
                    <h2>{selectedPlayer.name}</h2>
                    <small>#{selectedPlayer.squad_number ?? '—'} · {selectedPlayer.position || '—'}</small>
                  </div>
                  <strong>{signals.length}</strong>
                </div>

                <div className="training-signal-list">
                  {signals.map((signal) => (
                    <button key={signal.weakness} onClick={() => setManualWeakness(signal.weakness)}>
                      <span className={`training-domain-pill ${signal.domain}`}>{t[signal.domain]}</span>
                      <div>
                        <strong>{PLAYER_TRAINING[signal.weakness]?.label || signal.weakness}</strong>
                        <small>{signal.note || t.noCoachNotes}</small>
                      </div>
                      <b>{signal.severity}/5</b>
                    </button>
                  ))}
                  {!signals.length && <p className="muted">{t.noDevelopmentNeeds}</p>}
                </div>

                <label className="manual-training-weakness">
                  <span>{t.manualTrainingTarget}</span>
                  <select value={manualWeakness} onChange={(event) => setManualWeakness(event.target.value)}>
                    {Object.entries(PLAYER_TRAINING).map(([id, item]) => (
                      <option key={id} value={id}>{item.label}</option>
                    ))}
                  </select>
                </label>
              </article>

              <article className="metal-card training-method-card">
                <div className="section-title-line">
                  <div>
                    <p className="eyebrow">{t.trainingMethod}</p>
                    <h2>{method.label}</h2>
                  </div>
                  <Crosshair size={24} />
                </div>

                <section className="training-method-section">
                  <h3>{t.organisation}</h3>
                  <p>{method.organisation}</p>
                </section>

                <section className="training-method-columns">
                  <div>
                    <h3>{t.individualBlock}</h3>
                    <p>{method.individualUse}</p>
                  </div>
                  <div>
                    <h3>{t.teamApplication}</h3>
                    <p>{method.teamUse}</p>
                  </div>
                </section>

                <section className="training-method-section">
                  <h3>{t.coachingPoints}</h3>
                  <ul>
                    {method.coachingPoints.map((point) => <li key={point}>{point}</li>)}
                  </ul>
                </section>

                <section className="training-method-section">
                  <h3>{t.progression}</h3>
                  <p>{method.progression}</p>
                </section>

                <section className="training-load-strip">
                  <Dumbbell size={18} />
                  <div>
                    <span>{t.workRest}</span>
                    <strong>{method.workRest}</strong>
                  </div>
                </section>

                <a className="training-source-link" href={method.sourceUrl} target="_blank" rel="noreferrer">
                  <Link2 size={18} />
                  <div>
                    <span>{t.externalMethodReference}</span>
                    <strong>{method.sourceLabel}</strong>
                  </div>
                  <ExternalLink size={17} />
                </a>
              </article>
            </section>
          )}
        </>
      ) : (
        <section className="team-training-layout">
          <aside className="metal-card team-topic-selector">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.teamWeakness}</p>
                <h2>{t.teamTraining}</h2>
              </div>
              <Shield size={23} />
            </div>

            <div className="team-topic-list">
              {TEAM_TOPICS.map((topic) => (
                <button
                  key={topic.id}
                  className={teamTopic.id === topic.id ? 'selected' : ''}
                  onClick={() => setTeamTopicId(topic.id)}
                >
                  <span className={`training-domain-pill ${topic.domain}`}>{topic.domain === 'set_piece' ? t.setPieces : t[topic.domain]}</span>
                  <strong>{topic.label}</strong>
                  <ChevronRight size={17} />
                </button>
              ))}
            </div>
          </aside>

          <article className="metal-card team-training-plan">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.teamSession}</p>
                <h2>{teamTopic.label}</h2>
              </div>
              <Sparkles size={24} />
            </div>

            <section className="training-method-section">
              <h3>{t.objective}</h3>
              <p>{teamTopic.objective}</p>
            </section>

            <section className="training-method-section">
              <h3>{t.sessionStructure}</h3>
              <ol>
                {teamTopic.sessionStructure.map((item) => <li key={item}>{item}</li>)}
              </ol>
            </section>

            <section className="training-method-section">
              <h3>{t.coachingPoints}</h3>
              <ul>
                {teamTopic.coachingPoints.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>

            <a className="training-source-link" href={teamTopic.sourceUrl} target="_blank" rel="noreferrer">
              <BookOpen size={18} />
              <div>
                <span>{t.externalMethodReference}</span>
                <strong>{teamTopic.sourceLabel}</strong>
              </div>
              <ExternalLink size={17} />
            </a>

            <p className="training-link-note">{t.linkFirstTrainingNote}</p>
          </article>
        </section>
      )}

      <DevelopmentProgress />
      <SessionLibrary />
    </div>
  );
}
